import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Send, Mail, Eye, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function NewsletterPage() {
  const [subscribers, setSubscribers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [sends, setSends] = useState([]);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");

  useEffect(() => {
    api.get("/newsletter/subscribers").then(r => setSubscribers(r.data)).catch(() => {});
    api.get("/articles?limit=20").then(r => setArticles(r.data.articles || [])).catch(() => {});
    api.get("/newsletter/sends").then(r => setSends(r.data)).catch(() => {});
  }, []);

  const toggleArticle = (id) => {
    setSelectedArticles(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  };

  const handleSend = async () => {
    if (!subject) { toast.error("Subject required"); return; }
    if (!content && selectedArticles.length === 0) { toast.error("Add content or select articles"); return; }
    setSending(true);
    try {
      const res = await api.post("/newsletter/send", {
        subject,
        html_content: content,
        article_ids: selectedArticles,
      });
      toast.success(res.data.detail);
      setSubject("");
      setContent("");
      setSelectedArticles([]);
      api.get("/newsletter/sends").then(r => setSends(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send newsletter");
    }
    setSending(false);
  };

  return (
    <div data-testid="newsletter-page">
      <div className="mb-6">
        <h1 className="text-3xl font-serif font-bold text-foreground">Newsletter</h1>
        <p className="text-sm font-sans text-muted-foreground mt-1">
          <Mail className="h-4 w-4 inline mr-1" />
          {subscribers.length} subscriber{subscribers.length !== 1 ? "s" : ""}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-serif text-lg">Compose Newsletter</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="font-sans text-sm">Subject *</Label>
                <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Weekly Medical News Digest" className="mt-1" data-testid="newsletter-subject" />
              </div>
              <div>
                <Label className="font-sans text-sm">Message (optional intro)</Label>
                <Textarea value={content} onChange={e => setContent(e.target.value)} rows={4} placeholder="This week in medical news..." className="mt-1 font-sans" data-testid="newsletter-content" />
              </div>

              <Separator />

              <div>
                <Label className="font-sans text-sm mb-3 block">Include Articles</Label>
                <div className="max-h-64 overflow-y-auto space-y-2 border border-slate-200 dark:border-slate-700 p-3">
                  {articles.map(art => (
                    <label key={art.id} className="flex items-start gap-3 p-2 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                      <Checkbox
                        checked={selectedArticles.includes(art.id)}
                        onCheckedChange={() => toggleArticle(art.id)}
                        data-testid={`select-article-${art.id}`}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-sans font-medium text-foreground line-clamp-1">{art.title}</p>
                        <p className="text-xs font-sans text-muted-foreground">{art.reading_time}</p>
                      </div>
                    </label>
                  ))}
                </div>
                {selectedArticles.length > 0 && (
                  <p className="text-xs font-sans text-[#1E88E5] mt-2">{selectedArticles.length} article{selectedArticles.length !== 1 ? "s" : ""} selected</p>
                )}
              </div>

              <Button
                onClick={handleSend}
                disabled={sending}
                className="w-full bg-[#1E88E5] hover:bg-[#1565C0] font-sans h-11"
                data-testid="send-newsletter-btn"
              >
                <Send className="h-4 w-4 mr-2" /> {sending ? "Sending..." : `Send to ${subscribers.length} Subscriber${subscribers.length !== 1 ? "s" : ""}`}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* History */}
        <div className="space-y-6">
          <Card className="border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-serif text-lg">Send History</CardTitle>
            </CardHeader>
            <CardContent>
              {sends.length === 0 ? (
                <p className="text-sm font-sans text-muted-foreground text-center py-4">No newsletters sent yet</p>
              ) : (
                <div className="space-y-3">
                  {sends.slice(0, 10).map(s => (
                    <div key={s.id} className="border-b border-slate-100 dark:border-slate-700 pb-3 last:border-0">
                      <p className="text-sm font-sans font-medium text-foreground">{s.subject}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs font-sans text-muted-foreground">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        <span>{s.sent} sent</span>
                        {s.failed > 0 && <><AlertCircle className="h-3 w-3 text-red-500" /><span>{s.failed} failed</span></>}
                      </div>
                      <p className="text-[10px] font-sans text-muted-foreground mt-1">
                        {new Date(s.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-serif text-lg">Subscribers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {subscribers.map(sub => (
                  <p key={sub.id} className="text-xs font-sans text-muted-foreground py-1 border-b border-slate-100 dark:border-slate-700 last:border-0 truncate">
                    {sub.email}
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
